import {
  useUpdateWorkerCredentialConfiguration,
  type WorkerCredentialConfiguration,
} from "@src/appV2/FacilityProfile/api/useUpdateWorkerCredentialConfiguration";
import { useGetAttendanceVerification } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/api/useGetAttendanceVerification";
import { hasEnabledMethodThatRequiresCredentials } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/types";
import { useToast } from "@src/appV2/lib";
import { useState } from "react";

import { type SettingsPermissions } from "../../types";
import { WorkerCredentialsSectionView } from "./WorkerCredentialsSectionView";
import { useTu } from "@src/appV2/i18n/useTu";

interface WorkerCredentialsSectionContainerProps {
  guardSettingsAction: (action: () => void) => void;
  permissions: SettingsPermissions;
  workerCredentialConfiguration?: WorkerCredentialConfiguration;
  workplaceId: string;
  onSettingsChanged: () => void;
}

const DEFAULT_CONFIGURATION: WorkerCredentialConfiguration = {
  charting: { enabled: false },
  timekeeping: { enabled: false },
  onboardingView: {
    enabled: false,
    shiftStartingSoonThreshold: { value: 24, unit: "hours" },
  },
};

export function WorkerCredentialsSectionContainer({
  guardSettingsAction,
  permissions,
  workerCredentialConfiguration,
  workplaceId,
  onSettingsChanged,
}: WorkerCredentialsSectionContainerProps) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const { data: attendanceVerification } = useGetAttendanceVerification(workplaceId);

  const config = workerCredentialConfiguration ?? DEFAULT_CONFIGURATION;

  const [configuration, setConfiguration] = useState<WorkerCredentialConfiguration>(config);

  const { isLoading: isUpdating, mutateAsync: updateConfiguration } =
    useUpdateWorkerCredentialConfiguration();

  const handleUpdate = async (
    updated: WorkerCredentialConfiguration,
    rollback: WorkerCredentialConfiguration
  ) => {
    setConfiguration(updated);
    try {
      await updateConfiguration({
        workplaceId,
        workerCredentialConfiguration: updated,
      });
      showSuccessToast(tu("Worker credential configuration updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating worker credential configuration. Please try again later."));
      setConfiguration(rollback);
    }
  };

  return (
    <WorkerCredentialsSectionView
      configuration={configuration}
      isUpdating={isUpdating}
      permissions={permissions}
      onChartingToggle={(checked) => {
        guardSettingsAction(() => {
          void handleUpdate({ ...configuration, charting: { enabled: checked } }, configuration);
        });
      }}
      onTimekeepingToggle={(checked) => {
        guardSettingsAction(() => {
          if (
            !checked &&
            hasEnabledMethodThatRequiresCredentials(
              attendanceVerification?.data.attributes.methods ?? []
            )
          ) {
            showErrorToast(
              "Cannot disable timekeeping while there are enabled timekeeping methods. Please disable them first."
            );
            return;
          }
          const { credentialVariant } = configuration.timekeeping;
          void handleUpdate(
            {
              ...configuration,
              timekeeping: {
                ...configuration.timekeeping,
                enabled: checked,
                // Normalize the `optionalEnumWithFallback` sentinel so the unknown value
                // never round-trips to the backend (matches `getCredentialVariant`).
                credentialVariant:
                  credentialVariant === "UNRECOGNIZED_" ? undefined : credentialVariant,
              },
            },
            configuration
          );
        });
      }}
      onOnboardingViewToggle={(checked) => {
        guardSettingsAction(() => {
          void handleUpdate(
            {
              ...configuration,
              onboardingView: { ...configuration.onboardingView, enabled: checked },
            },
            configuration
          );
        });
      }}
      onShiftStartingSoonThresholdChange={(value) => {
        guardSettingsAction(() => {
          void handleUpdate(
            {
              ...configuration,
              onboardingView: {
                ...configuration.onboardingView,
                shiftStartingSoonThreshold: { value, unit: "hours" as const },
              },
            },
            configuration
          );
        });
      }}
      onCredentialVariantChange={(variant) => {
        guardSettingsAction(() => {
          void handleUpdate(
            {
              ...configuration,
              timekeeping: { ...configuration.timekeeping, credentialVariant: variant },
            },
            configuration
          );
        });
      }}
    />
  );
}
